'use client';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import React from 'react';
import Cookies from 'js-cookie';
import { RESTRICT_AGE } from '@/constant/data';

type ModalRestrictAgeProps = {
  title?: string;
  description?: string;
  age?: number;
  confirm_label?: string;
  cancel_label?: string;
  image?: string;
  confirm_button_background_color?: string;
  confirm_label_text_color?: string;
  cancel_button_background_color?: string;
  cancel_label_text_color?: string;
  createCookieRestrictAge: (isActive: boolean) => Promise<void>;
};

export default function ModalRestrictAge({
  title = RESTRICT_AGE.title,
  description = RESTRICT_AGE.description,
  age = RESTRICT_AGE.age,
  confirm_label = RESTRICT_AGE.confirm_label,
  cancel_label = RESTRICT_AGE.cancel_label,
  image,
  confirm_button_background_color = RESTRICT_AGE.confirm_button_background_color,
  confirm_label_text_color = RESTRICT_AGE.confirm_label_text_color,
  cancel_button_background_color = RESTRICT_AGE.cancel_button_background_color,
  cancel_label_text_color = RESTRICT_AGE.cancel_label_text_color,
  createCookieRestrictAge,
}: ModalRestrictAgeProps) {
  const query = useSearchParams();
  const isPreview = query.get('isPreview') === 'true';

  const [hydrated, setHydrated] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [showSorryMessage, setShowSorryMessage] = React.useState<boolean>(false);

  React.useEffect(() => {
    setHydrated(true);
  }, []);

  React.useEffect(() => {
    if (!hydrated) return;
    const ageConfirmed = Cookies.get('ageRestrictConfirm');
    if (!ageConfirmed && !isPreview) {
      setIsOpen(true);
    }
  }, [hydrated, isPreview]);

  const handleActionConfirm = () => {
    createCookieRestrictAge(true);
    setIsOpen(false);
    window.dispatchEvent(new Event('ageConfirmed'));
  };

  const handleActionCancel = () => {
    setShowSorryMessage(true);
  };

  if (!hydrated) return null;

  return (
    <>
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent
          id='modal-restict-age'
          className='z-[999999] max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl'
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-nosnippet
        >
          <AlertDialogHeader>
            {!showSorryMessage && (
              <Image
                src={image || '/assets/logo.png'}
                alt='Age Restricted'
                width={200}
                height={200}
                className='py-4'
              />
            )}
            <AlertDialogTitle className='text-2xl font-normal'>
              {showSorryMessage ? 'SORRY!' : title}
            </AlertDialogTitle>
            <AlertDialogDescription className='flex items-center justify-center text-justify'>
              {showSorryMessage ? (
                'You are not old enough to view the site...'
              ) : (
                <div dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!showSorryMessage && (
            <AlertDialogFooter>
              <div className='flex w-full flex-col gap-3'>
                <button
                  className='confirm_button_style w-full rounded-sm border border-slate-300 py-4 font-normal disabled:cursor-not-allowed'
                  onClick={handleActionConfirm}
                >
                  {confirm_label} {age}+
                </button>

                <button
                  className='cancel_button_style w-full rounded-sm border border-slate-300 py-4 font-normal shadow-md disabled:cursor-not-allowed'
                  onClick={handleActionCancel}
                >
                  {cancel_label} {age}+
                </button>
              </div>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
      <style jsx>
        {`
          .confirm_button_style {
            background: ${confirm_button_background_color};
            color: ${confirm_label_text_color};
          }
          .cancel_button_style {
            background: ${cancel_button_background_color};
            color: ${cancel_label_text_color};
          }
        `}
      </style>
    </>
  );
}
